/* eslint-disable @repo/no-style-props, @repo/no-abstracted-overlay-trigger */
import * as React from "react";
import { Check, Loader2 } from "lucide-react";
import { DropdownIndicator } from "@/src/components/design-system/DropdownIndicator/DropdownIndicator";

import { cn } from "@/src/utils/tailwind";
import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import {
  InputCommand,
  InputCommandEmpty,
  InputCommandGroup,
  InputCommandInput,
  InputCommandItem,
  InputCommandList,
  InputCommandSeparator,
} from "@/src/components/ui/input-command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { Separator } from "@/src/components/ui/separator";
import { type FilterOption } from "@langfuse/shared";
import { Input } from "@/src/components/ui/input";
import { useRef, useState, useMemo, useCallback } from "react";
import { PropertyHoverCard } from "@/src/features/widgets/components/WidgetPropertySelectItem";

/** compactSelectAllLimit is the most options a compact select shows Select All for. */
const compactSelectAllLimit = 100;

const getFreeTextInput = (
  isCustomSelectEnabled: boolean,
  values: string[],
  optionValues: Set<string>,
): string | undefined =>
  isCustomSelectEnabled
    ? Array.from(values.values()).find((value) => !optionValues.has(value))
    : undefined;

export function MultiSelect({
  title,
  label,
  values,
  onValueChange,
  options,
  className,
  disabled,
  isCustomSelectEnabled = false,
  labelTruncateCutOff = 2,
  chipsOnly = false,
  onOpenChange,
  isLoading = false,
}: {
  title?: string;
  label?: string;
  values: string[];
  onValueChange: (values: string[]) => void;
  options: FilterOption[] | readonly FilterOption[];
  className?: string;
  disabled?: boolean;
  isCustomSelectEnabled?: boolean;
  labelTruncateCutOff?: number;
  /** chipsOnly hides the placeholder/separator once values are selected, showing just the chips and chevron. */
  chipsOnly?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
}) {
  const selectedValues = useMemo(() => new Set(values), [values]);
  const optionValues = new Set(options.map((option) => option.value));
  const freeTextInput = getFreeTextInput(
    isCustomSelectEnabled,
    values,
    optionValues,
  );
  const [freeText, setFreeText] = useState(freeTextInput || "");

  // Merge options with selected values that might not be in options
  // This ensures selected values are always visible and removable
  const mergedOptions = useMemo(() => {
    const optionSet = new Set(options.map((o) => o.value));
    const missingSelectedOptions: FilterOption[] = values
      .filter((v) => !optionSet.has(v) && v.length > 0)
      .map((v) => ({ value: v }));
    return [...options, ...missingSelectedOptions];
  }, [options, values]);

  const selectableOptions = useMemo(
    () => mergedOptions.filter((option) => option.value.length > 0),
    [mergedOptions],
  );

  const showSelectAll =
    selectableOptions.length > 0 &&
    !(chipsOnly && selectableOptions.length > compactSelectAllLimit);

  const allSelectedState = useMemo(() => {
    if (selectableOptions.length === 0) return false;
    return selectableOptions.every((option) =>
      selectedValues.has(option.value),
    );
  }, [selectableOptions, selectedValues]);

  const handleSelectAll = useCallback(() => {
    const newSelectedValues = new Set(selectedValues);
    if (allSelectedState) {
      // Deselect all selectable options
      selectableOptions.forEach((option) =>
        newSelectedValues.delete(option.value),
      );
    } else {
      // Select all selectable options
      selectableOptions.forEach((option) =>
        newSelectedValues.add(option.value),
      );
    }
    const filterValues = Array.from(newSelectedValues);
    onValueChange(filterValues.length ? filterValues : []);
  }, [allSelectedState, selectableOptions, selectedValues, onValueChange]);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleDebouncedChange = (value: string) => {
    const freeTextInput = getFreeTextInput(
      isCustomSelectEnabled,
      values,
      optionValues,
    );

    if (!!freeTextInput) {
      selectedValues.delete(freeTextInput);
      selectedValues.add(value);
      selectedValues.delete("");
      const filterValues = Array.from(selectedValues);
      onValueChange(filterValues.length ? filterValues : []);
    }
  };

  function getSelectedOptions() {
    const selectedOptions = mergedOptions.filter(
      ({ value }) =>
        selectedValues.has(value) &&
        (!freeTextInput || value !== freeTextInput),
    );
    const liveCustomOption: FilterOption[] =
      freeTextInput && freeText ? [{ value: freeText }] : [];

    return [...selectedOptions, ...liveCustomOption];
  }

  const selectedBadges =
    selectedValues.size > labelTruncateCutOff ? (
      <Badge variant="secondary" className="rounded-sm px-1 font-normal">
        {selectedValues.size} selected
      </Badge>
    ) : (
      getSelectedOptions().map((option) => {
        const displayValue =
          option.displayValue ??
          (option.value === "" ? "(empty)" : option.value);
        return (
          <Badge
            variant="secondary"
            key={option.value}
            className={cn(
              "min-w-0 rounded-sm px-1 font-normal",
              option.value === "" && "italic",
            )}
          >
            <span className="truncate" title={displayValue}>
              {displayValue}
            </span>
          </Badge>
        );
      })
    );

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            // min-w-0 + overflow-hidden: the trigger must never grow past its
            // container — wide selected values truncate instead.
            "border-input ring-offset-background placeholder:text-foreground-tertiary focus:ring-ring flex h-8 w-full min-w-0 items-center justify-between gap-x-2 overflow-hidden rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          disabled={disabled}
        >
          {chipsOnly && selectedValues.size > 0 ? (
            <>
              <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                {selectedBadges}
              </div>
              <DropdownIndicator />
            </>
          ) : (
            <>
              {label ?? "Select"}
              <DropdownIndicator />
              {selectedValues.size > 0 && (
                <>
                  <Separator orientation="vertical" className="mr-auto h-4" />
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal lg:hidden"
                  >
                    {selectedValues.size}
                  </Badge>
                  <div className="hidden min-w-0 space-x-1 overflow-hidden lg:flex">
                    {selectedBadges}
                  </div>
                </>
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="ph-no-capture w-[200px] p-0" align="center">
        <InputCommand>
          <InputCommandInput
            placeholder={title}
            variant="bottom"
            disabled={isLoading}
          />
          <InputCommandList>
            {isLoading && (
              <div
                role="status"
                className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm"
              >
                <Loader2
                  className="icon-base animate-spin"
                  aria-hidden="true"
                />
                Loading…
              </div>
            )}
            {/* if isCustomSelectEnabled we always show custom select hence never empty */}
            {!isCustomSelectEnabled && !isLoading && (
              <InputCommandEmpty>No results found.</InputCommandEmpty>
            )}
            <InputCommandGroup>
              {showSelectAll && (
                <>
                  <InputCommandItem
                    key="select-all"
                    onSelect={handleSelectAll}
                    disabled={isLoading}
                  >
                    <div
                      className={cn(
                        "border-control-border mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        allSelectedState
                          ? "bg-control-fill border-control-fill text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="icon-base" />
                    </div>
                    <div className="font-bold">
                      {allSelectedState ? "Deselect All" : "Select All"}
                    </div>
                  </InputCommandItem>
                  <InputCommandSeparator />
                </>
              )}
              {mergedOptions.map((option) => {
                if (option.value.length === 0) return;
                const isSelected = selectedValues.has(option.value);
                const displayValue =
                  option.displayValue ??
                  (option.value === "" ? "(empty)" : option.value);
                const displayTitle =
                  option.displayValue ??
                  (option.value === "" ? "(empty)" : option.value);

                const commandItem = (
                  <InputCommandItem
                    key={option.value}
                    disabled={isLoading}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      onValueChange(filterValues.length ? filterValues : []);
                    }}
                  >
                    <div
                      className={cn(
                        "border-control-border mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        isSelected
                          ? "bg-control-fill border-control-fill text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="icon-base" />
                    </div>
                    <div
                      className={cn(
                        "overflow-x-hidden text-ellipsis whitespace-nowrap",
                        option.value === "" && "text-muted-foreground italic",
                      )}
                      title={displayTitle}
                    >
                      {displayValue}
                    </div>
                    {option.count !== undefined ? (
                      <span className="ml-auto flex h-4 w-4 items-center justify-center pl-1 font-mono text-xs">
                        {option.count}
                      </span>
                    ) : null}
                  </InputCommandItem>
                );

                return option.description ? (
                  <PropertyHoverCard
                    key={option.value}
                    label={displayValue}
                    description={option.description}
                  >
                    {commandItem}
                  </PropertyHoverCard>
                ) : (
                  commandItem
                );
              })}
            </InputCommandGroup>
            {isCustomSelectEnabled && (
              <InputCommandGroup forceMount={true}>
                <InputCommandSeparator />
                <InputCommandItem
                  key="freeTextField"
                  disabled={isLoading}
                  onSelect={() => {
                    const freeTextInput = getFreeTextInput(
                      isCustomSelectEnabled,
                      values,
                      optionValues,
                    );

                    if (!!freeTextInput) {
                      selectedValues.delete(freeTextInput);
                    } else {
                      selectedValues.add(freeText);
                    }
                    selectedValues.delete("");
                    const filterValues = Array.from(selectedValues);
                    onValueChange(filterValues.length ? filterValues : []);
                  }}
                >
                  <div
                    className={cn(
                      "border-control-border mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                      getFreeTextInput(
                        isCustomSelectEnabled,
                        values,
                        optionValues,
                      ) ||
                        (optionValues.has(freeText) &&
                          selectedValues.has(freeText))
                        ? "bg-control-fill border-control-fill text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <Check className="icon-base" />
                  </div>
                  <Input
                    type="text"
                    disabled={isLoading}
                    value={freeText}
                    onChange={(e) => {
                      setFreeText(e.target.value);
                      if (debounceTimeout.current) {
                        clearTimeout(debounceTimeout.current);
                      }
                      debounceTimeout.current = setTimeout(() => {
                        handleDebouncedChange(e.target.value);
                      }, 500);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    placeholder="Enter custom value"
                    className="h-6 w-full rounded-none border-t-0 border-r-0 border-b-2 border-l-0 border-dotted p-0 text-sm"
                  />
                </InputCommandItem>
              </InputCommandGroup>
            )}
            {selectedValues.size > 0 && (
              <>
                <InputCommandSeparator />
                <InputCommandGroup>
                  <InputCommandItem
                    disabled={isLoading}
                    onSelect={() => onValueChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </InputCommandItem>
                </InputCommandGroup>
              </>
            )}
          </InputCommandList>
        </InputCommand>
      </PopoverContent>
    </Popover>
  );
}
